import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import { imageUrl } from '../lib/images';
import {
  fetchSide,
  fetchVaerdier,
  fetchVirksomhedsoplysninger,
  FALLBACK_SIDER,
  FALLBACK_VAERDIER,
  FALLBACK_VIRKSOMHED
} from '../lib/cms';

export default function OmOs() {
  const [sideData, setSideData] = useState(FALLBACK_SIDER['om-os'] || {});
  const [vaerdier, setVaerdier] = useState(FALLBACK_VAERDIER);
  const [virksomhed, setVirksomhed] = useState(FALLBACK_VIRKSOMHED);

  useEffect(() => {
    async function loadData() {
      const [sRes, vRes, cRes] = await Promise.all([
        fetchSide('om-os'),
        fetchVaerdier(),
        fetchVirksomhedsoplysninger()
      ]);
      if (sRes) setSideData(sRes);
      if (Array.isArray(vRes) && vRes.length > 0) setVaerdier(vRes);
      if (cRes) setVirksomhed(cRes);
    }
    loadData();
  }, []);

  const heroImg = sideData.billede?.[0] || '/_assets/imgi-21-646887628-122121748119136715-5214852611206569786-n.jpg';
  return (
    <div className="bg-slate-50 min-h-screen">
      <PageMeta
        title={sideData.titel || "Om Duus Rustfri - Duus Rustfri"}
        description={sideData.meta_beskrivelse || "Lær Duus Rustfri at kende. Enkeltmandsvirksomhed i Vissenbjerg med fokus på ærligt håndværk, direkte kontakt og specialløsninger i stål og alu."}
      />

      {/* Hero Header */}
      <section className="bg-slate-950 text-white py-16 sm:py-24 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 border border-slate-800 text-sky-400 text-xs font-semibold uppercase tracking-wider mb-4">
              <i className="fa-solid fa-user-gear text-sky-400" aria-hidden="true" />
              <span>{sideData.badge || 'Enkeltmandsvirksomhed på Fyn'}</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white mb-4">
              {sideData.overskrift || 'Om Duus Rustfri'}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              {sideData.underoverskrift || 'Vi sætter en ære i det gode, ærlige håndværk. Her er ingen fordyrende mellemled – du taler direkte med den smed, der fremstiller dine emner.'}
            </p>
          </div>
        </div>
      </section>

      {/* Story & Philosophy */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Text side */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-mono font-bold uppercase text-sky-700 bg-sky-50 px-3 py-1 rounded-full border border-sky-200">
                Stolthed i faget
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-950 tracking-tight">
                Alt fra små reparationer til skræddersyede specialløsninger
              </h2>

              <p className="text-slate-600 leading-relaxed text-base sm:text-lg">
                Duus Rustfri er en fynsk enkeltmandsvirksomhed beliggende i Vissenbjerg. Grundtanken er enkel: Hver eneste svejsning og hvert eneste stykke forarbejdet metal skal holde til hverdagens krav og leve op til de højeste standarder for godt håndværk.
              </p>

              <p className="text-slate-600 leading-relaxed text-base">
                Vores spidskompetence er arbejdet med <strong>rustfrit stål, sort stål og aluminium</strong>. Vi løser opgaver for både landbrug, produktionsvirksomheder, mindre håndværkere og private husejere. Uanset om opgaven drejer sig om en akut udbedring af et knækket beslag, serieproduktion af foderkrybber eller udvikling af et specialværktøj, går vi til opgaven med samme grundighed.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4 border-t border-slate-200">
                <div className="space-y-1">
                  <div className="text-2xl font-black text-slate-950">1:1</div>
                  <div className="text-xs text-slate-500 font-medium">Direkte kontakt med håndværkeren</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-black text-slate-950">100%</div>
                  <div className="text-xs text-slate-500 font-medium">Tilpasset dine mål og tegninger</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-black text-slate-950">3</div>
                  <div className="text-xs text-slate-500 font-medium">Hovedmaterialer: Rustfri, sort & alu</div>
                </div>
              </div>
            </div>

            {/* Photo side with actual workshop photo */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200 bg-slate-900 aspect-[4/5]">
                <img
                  src={imageUrl(
                    heroImg,
                    'width=800,quality=85,format=auto'
                  )}
                  alt="Værkstedsmiljø hos Duus Rustfri med svejset rør i skruestik og vaterpas"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <p className="text-xs font-mono text-sky-400 uppercase tracking-wider mb-1">
                    Værkstedet i {virksomhed.postnr_by?.split(' ')?.[1] || 'Vissenbjerg'}
                  </p>
                  <p className="text-sm font-semibold text-slate-200">
                    Præcision og kvalitetskontrol i enhver svejsning.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Core Pillars */}
      <section className="py-16 sm:py-24 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-extrabold text-slate-950 tracking-tight">
              {sideData.sektion_3_overskrift || 'Hvad kan du forvente som kunde?'}
            </h2>
            <p className="mt-3 text-slate-600">
              {sideData.sektion_3_tekst || 'Vores værdier er forankret i den klassiske smedetradition: troværdighed, præcision og ordentlige aftaler.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {vaerdier.map((v, vIdx) => {
              const justeringTerm = v.terms?.tekstjustering?.[0];
              const alignClass = justeringTerm === 'centreret' ? 'text-center items-center' : justeringTerm === 'hoejre' ? 'text-right items-end' : 'text-left items-start';

              return (
                <div key={v.id || vIdx} className={`p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-3 flex flex-col ${alignClass}`}>
                  <div className="w-12 h-12 rounded-xl bg-slate-900 text-sky-400 flex items-center justify-center text-lg flex-shrink-0">
                    <i className={`fa-solid ${v.ikon || 'fa-check'} text-sky-400`} aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-950">{v.titel}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {v.beskrivelse}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Workshop Gallery Snapshot */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <span className="text-xs uppercase font-mono tracking-wider text-sky-700">Dokumenteret arbejde</span>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-950">Billeder fra værkstedet</h2>
            </div>
            <Link
              to="/referencer"
              className="text-sm font-semibold text-sky-700 hover:text-sky-800 transition inline-flex items-center gap-1.5"
            >
              <span>Se alle referencer & projekter</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-2xl overflow-hidden aspect-square bg-slate-900 border border-slate-200 shadow-sm">
              <img
                src={imageUrl('/_assets/imgi-21-699751925-122129448003136715-150113504640350210-n.jpg', 'width=400,height=400,fit=cover,quality=80,format=auto')}
                alt="Flaskeløfter i rustfrit stål"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden aspect-square bg-slate-900 border border-slate-200 shadow-sm">
              <img
                src={imageUrl('/_assets/imgi-21-647015786-122121748197136715-8753079384640241207-n.jpg', 'width=400,height=400,fit=cover,quality=80,format=auto')}
                alt="Svejsede rør med gevindstudse"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden aspect-square bg-slate-900 border border-slate-200 shadow-sm">
              <img
                src={imageUrl('/_assets/imgi-21-586549557-122097119469136715-5406239360448565138-n.jpg', 'width=400,height=400,fit=cover,quality=80,format=auto')}
                alt="Fodertrug i metal"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden aspect-square bg-slate-900 border border-slate-200 shadow-sm">
              <img
                src={imageUrl('/_assets/imgi-21-648091554-122121748029136715-4881053340309711990-n.jpg', 'width=400,height=400,fit=cover,quality=80,format=auto')}
                alt="Værkstedsbord med fittings og emner"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Direct Contact Bar */}
      <section className="py-16 sm:py-24 bg-slate-950 text-white border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center mx-auto text-sky-400 text-2xl">
            <i className="fa-solid fa-handshake" aria-hidden="true" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
            Klar til at drøfte dit næste stålprojekt?
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto text-base">
            Kontakt mig for en uforpligtende snak om materialer, udformning eller reparation. Jeg rådgiver gerne ud fra mange års erfaring.
          </p>

          <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 w-full sm:w-auto">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-sky-600 to-blue-700 hover:from-sky-500 hover:to-blue-600 text-white font-semibold text-sm shadow-xl transition text-center"
            >
              <span>Send en forespørgsel</span>
              <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
            </Link>

            <a
              href={`tel:${virksomhed.telefon.replace(/\s+/g, '')}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-semibold text-sm shadow-lg shadow-sky-950/50 transition whitespace-nowrap"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>Ring på {virksomhed.telefon}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
